import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Play, FileText, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExamGuidePage = () => {
  const { settings } = useSiteSettings();
  const whatsapp = settings?.whatsapp || '85295858258';
  const [examRoutes, setExamRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await axios.get(`${API_URL}/exam-routes?published_only=true`);
        setExamRoutes(response.data);
      } catch (error) {
        console.error('Failed to fetch exam routes:', error);
      }
    };
    fetchRoutes();
  }, []);

  const routesWithVideos = examRoutes.filter(r => r.routes && r.routes.length > 0);
  const routesWithoutVideos = examRoutes.filter(r => !r.routes || r.routes.length === 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
            考試攻略
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
            各考場路線影片及攻略，助你輕鬆應試
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Exam Route Grid */}
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-blue-600" />
            路線列表
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-12">
            {examRoutes.map((route, index) => (
              <button
                key={route._id || route.id || index}
                onClick={() => {
                  if (route.routes.length > 0) {
                    setSelectedRoute(selectedRoute?.name === route.name ? null : route);
                    setActiveTab(0);
                  }
                }}
                className={`relative p-4 rounded-xl text-center font-semibold transition-all duration-200 ${
                  selectedRoute?.name === route.name
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : route.routes.length > 0
                    ? 'bg-white shadow-md hover:shadow-lg hover:-translate-y-0.5 text-gray-900 border border-gray-100'
                    : 'bg-white shadow-sm text-gray-500 border border-gray-100'
                }`}
              >
                <span className="block text-lg">{route.name}</span>
                <span className="block text-xs mt-1 opacity-70">{route.area}</span>
                {route.routes.length > 0 && (
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Play className="h-2.5 w-2.5 text-white fill-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Selected Route Video Player */}
          {selectedRoute && selectedRoute.routes.length > 0 && (
            <Card className="overflow-hidden border-0 shadow-xl bg-white mb-12">
              <div className="h-1.5 bg-gradient-to-r from-blue-500 to-yellow-500" />
              <CardContent className="p-6 md:p-8">
                <h3 className="text-2xl font-bold mb-4">{selectedRoute.name}</h3>

                {/* Route Tabs */}
                <div className="flex gap-2 mb-6">
                  {selectedRoute.routes.map((route, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        activeTab === index
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {route.label}
                    </button>
                  ))}
                </div>

                {/* Video */}
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${selectedRoute.routes[activeTab].videoId}`}
                    title={`${selectedRoute.name} ${selectedRoute.routes[activeTab].label}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Videos Note */}
          {routesWithoutVideos.length > 0 && (
            <div className="bg-blue-50 rounded-2xl p-6 md:p-8 mb-12">
              <div className="flex items-start gap-3">
                <FileText className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">更多考場資訊</h3>
                  <p className="text-sm text-gray-600">
                    {routesWithoutVideos.map(r => r.name).join('、')}等考場的路線攻略，歡迎透過 WhatsApp 向我們查詢，師傅會為你詳細講解。
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">需要更多考試攻略？</h2>
            <p className="text-lg text-muted-foreground mb-8">
              我們的師傅會為你詳細講解各考場路線及攻略要點
            </p>
            <Button asChild size="lg" className="rounded-full bg-green-500 hover:bg-green-600 text-white px-8">
              <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp 查詢
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExamGuidePage;
